import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  return <div className="pt-32 pb-20" data-id="element-6651">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 text-center mb-16" data-id="element-6652">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-6653">
          Simple pricing for booking workflows that actually get used.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted mb-10" data-id="element-6654">
          Upgrade when you need more campaigns, more outreach, more team
          workflows, deeper contracts/payments, or venue-side programming
          features.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="flex items-center justify-center gap-4" data-id="element-6655">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-textMain' : 'text-textMuted'}`} data-id="element-6656">
            Monthly
          </span>
          <button onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')} className="w-14 h-7 bg-surface border border-border rounded-full relative transition-colors focus:outline-none" data-id="element-6657">
            <div className={`w-5 h-5 bg-primary rounded-full absolute top-0.5 transition-all ${billingCycle === 'annual' ? 'left-8' : 'left-1'}`} data-id="element-6658"></div>
          </button>
          <span className={`text-sm font-medium flex items-center gap-2 ${billingCycle === 'annual' ? 'text-textMain' : 'text-textMuted'}`} data-id="element-6659">
            Annually{' '}
            <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-full" data-id="element-6660">
              Save 20%
            </span>
          </span>
        </motion.div>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-6xl mx-auto px-6 mb-24" data-id="element-6661">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-6662">
          {/* Starter */}
          <div className="bg-surface border border-border rounded-2xl p-8 flex flex-col" data-id="element-6663">
            <h3 className="text-xl font-semibold mb-2" data-id="element-6664">Starter</h3>
            <p className="text-sm text-textMuted mb-6 h-10" data-id="element-6665">
              For solo artists and small teams getting organized.
            </p>
            <div className="mb-8" data-id="element-6666">
              <span className="text-4xl font-bold" data-id="element-6667">
                ${billingCycle === 'annual' ? '19' : '24'}
              </span>
              <span className="text-textMuted" data-id="element-6668">/mo</span>
            </div>
            <Link to="/onboarding" className="w-full py-3 px-4 bg-background border border-border text-center rounded-lg font-medium hover:bg-surfaceHover transition-colors mb-8" data-id="element-6669">
              Start Free Trial
            </Link>
            <div className="space-y-4 flex-1" data-id="element-6670">
              <div className="text-sm font-medium mb-4" data-id="element-6671">Includes:</div>
              {['1 Active Campaign', 'Basic Venue Discovery', 'Deal Tracking Board', 'Standard Contract Templates', 'Email Integration'].map((feature, i) => <div key={i} className="flex items-start gap-3 text-sm text-textMuted" data-id="element-6672">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" data-id="element-6673" />
                  {feature}
                </div>)}
            </div>
          </div>

          {/* Pro */}
          <div className="bg-surface border-2 border-primary rounded-2xl p-8 flex flex-col relative transform md:-translate-y-4 shadow-[0_0_30px_rgba(245,158,11,0.1)]" data-id="element-6674">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-primary text-background text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-6675">
              Most Popular
            </div>
            <h3 className="text-xl font-semibold mb-2" data-id="element-6676">Pro</h3>
            <p className="text-sm text-textMuted mb-6 h-10" data-id="element-6677">
              For active artists, venues, and managers running real workflow
              volume.
            </p>
            <div className="mb-8" data-id="element-6678">
              <span className="text-4xl font-bold" data-id="element-6679">
                ${billingCycle === 'annual' ? '49' : '59'}
              </span>
              <span className="text-textMuted" data-id="element-6680">/mo</span>
            </div>
            <Link to="/onboarding" className="w-full py-3 px-4 bg-primary text-background text-center rounded-lg font-medium hover:bg-primaryHover transition-colors mb-8 shadow-[0_0_15px_rgba(245,158,11,0.3)]" data-id="element-6681">
              Start Free Trial
            </Link>
            <div className="space-y-4 flex-1" data-id="element-6682">
              <div className="text-sm font-medium mb-4" data-id="element-6683">
                Everything in Starter, plus:
              </div>
              {['Unlimited Campaigns', 'Advanced Routing Intelligence', 'Automated Outreach Sequences', 'Deposit & Payment Tracking', 'Lineup Conflict Management', 'AI Assistant (Standard)'].map((feature, i) => <div key={i} className="flex items-start gap-3 text-sm text-textMuted" data-id="element-6684">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" data-id="element-6685" />
                  {feature}
                </div>)}
            </div>
          </div>

          {/* Roster / Team */}
          <div className="bg-surface border border-border rounded-2xl p-8 flex flex-col" data-id="element-6686">
            <h3 className="text-xl font-semibold mb-2" data-id="element-6687">Roster / Team</h3>
            <p className="text-sm text-textMuted mb-6 h-10" data-id="element-6688">
              For agencies, managers, and venue teams who need seats and
              approvals.
            </p>
            <div className="mb-8" data-id="element-6689">
              <span className="text-4xl font-bold" data-id="element-6690">
                ${billingCycle === 'annual' ? '149' : '179'}
              </span>
              <span className="text-textMuted" data-id="element-6691">/mo</span>
              <div className="text-xs text-textMuted mt-1" data-id="element-6692">
                Includes 3 seats
              </div>
            </div>
            <Link to="/onboarding" className="w-full py-3 px-4 bg-background border border-border text-center rounded-lg font-medium hover:bg-surfaceHover transition-colors mb-8" data-id="element-6693">
              Start Free Trial
            </Link>
            <div className="space-y-4 flex-1" data-id="element-6694">
              <div className="text-sm font-medium mb-4" data-id="element-6695">
                Everything in Pro, plus:
              </div>
              {['Multi-Artist Roster View', 'Team Workload & Approvals', 'Custom Contract Builder', 'Advanced Financial Reporting', 'Shared Team Inboxes', 'Priority Support'].map((feature, i) => <div key={i} className="flex items-start gap-3 text-sm text-textMuted" data-id="element-6696">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" data-id="element-6697" />
                  {feature}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-6698">
        <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-6699">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4" data-id="element-6700">
          {[{
          q: 'Who is Booker for?',
          a: 'Booker is built for touring artists, independent venues, and boutique booking agencies who want to manage their entire workflow in one place.'
        }, {
          q: 'Can artists and venues both use it?',
          a: 'Yes. Booker provides role-specific interfaces. Artists see tools for routing and outreach, while venues see tools for programming and artist discovery.'
        }, {
          q: 'Does Booker replace a booking agent?',
          a: 'No. Booker is a tool, not an agent. It makes independent artists more efficient, and it makes professional agents vastly more productive.'
        }, {
          q: 'Can Booker handle contracts and deposits?',
          a: 'Yes. Pro and Team plans include full contract generation, e-signatures, and deposit tracking.'
        }].map((faq, i) => <div key={i} className="bg-surface border border-border rounded-xl p-6" data-id="element-6701">
              <h4 className="font-semibold mb-2" data-id="element-6702">{faq.q}</h4>
              <p className="text-sm text-textMuted" data-id="element-6703">{faq.a}</p>
            </div>)}
        </div>
      </section>
    </div>;
}